import type { EditorView } from '@codemirror/view';
import { atom, computed, map, type ReadableAtom } from 'nanostores';
import type { RefObject } from 'react';
import { toast } from 'react-toastify';
import { chatStore } from './chat';
import { EditorStore } from './editor';
import type { ContentHashOptions, CreateDirectoryOptions, File, FileMap, WriteFileOptions } from './files';
import { FilesStore } from './files';
import { PreviewsStore } from './previews';
import { SearchStore } from './search';
import { TerminalStore } from './terminal';
import type { EditorDocument, ScrollPosition } from '~/components/editor/codemirror/CodeMirrorEditor';
import type { PreviewRef } from '~/components/workbench/Preview';
import { SUPABASE_EDGE_FUNCTIONS_FOLDER } from '~/lib/constants';
import { forkProject } from '~/lib/projects/forkProject';
import {
  BaseShellAction,
  PipeAction,
  StartAction,
  SupabaseAction,
  type BoltAction,
  type ShellAction,
} from '~/lib/runtime/actions';
import { ActionsRunner } from '~/lib/runtime/actions-runner';
import type { ActionCallbackData, ArtifactCallbackData, PartialFileContent } from '~/lib/runtime/message-parser';
import { findDefaultSelectedFile } from '~/lib/stores/files/filemap';
import { projectStore } from '~/lib/stores/project';
import { webcontainer as webcontainerPromise } from '~/lib/webcontainer';
import type { AppFiles } from '~/types/files';
import { hmrPropertyFactory } from '~/utils/hmr';
import { invariant } from '~/utils/invariant';
import { logger } from '~/utils/logger';
import { withResolvers } from '~/utils/promises';
import { unreachable } from '~/utils/unreachable';
import { addWorkDir } from '~/utils/workDir';

const hmr = hmrPropertyFactory('WorkbenchStore');

export interface ArtifactState {
  id: string;
  messageId: string;
  slug: string;
  title: string;
  closed: boolean;
  runner: ActionsRunner;
}

export interface InspectedElement {
  tagName: string;
  html: string;
  file: globalThis.File;
  pathname: string;
}

export type TransferrableInspectedElement = Omit<InspectedElement, 'file'> & {
  buffer: ArrayBuffer;
};

export type ArtifactUpdateState = Pick<ArtifactState, 'title' | 'closed'>;

export type WorkbenchViewType = 'code' | 'preview' | 'database';

export type FileTreeViewType = 'files' | 'focused' | 'search';

export type WorkbenchWritableFile = Omit<File, 'content' | 'isLocked' | 'isTargeted' | 'lastModified'> & {
  content: string | Uint8Array;
};

const installs = new Set<string>();

const REMOVE_NPM_INSTALL_REGEX = /^\s*npm i(nstall)?\s*($|&&\s*)/gm;

const defaultView =
  (new URLSearchParams(globalThis.location?.search ?? '').get('default_view') as WorkbenchViewType) || 'preview';

export class WorkbenchStore {
  @hmr()
  artifacts = map<Record<string, ArtifactState>>({});

  @hmr()
  hasArtifacts = atom(false);

  @hmr()
  showWorkbench = atom(false);

  @hmr()
  currentView = atom<WorkbenchViewType>(defaultView);

  @hmr()
  currentFileTreeView = atom<FileTreeViewType>('files');

  @hmr()
  unsavedFiles = atom(new Set<string>());

  @hmr()
  isStreaming = atom(false);

  @hmr()
  isInspectorOpen = atom(false);

  @hmr()
  inspectedElement = atom<InspectedElement | null>(null);

  @hmr()
  pendingFixes = map<Record<string, boolean | undefined>>({});

  @hmr()
  isAnalyzing = atom(false);

  @hmr()
  isApplyingAnnotations = atom(false);

  @hmr()
  previewRef: RefObject<PreviewRef> | undefined;

  #filesStore = new FilesStore(webcontainerPromise);

  previewsStore = new PreviewsStore(webcontainerPromise, this.#filesStore);

  searchStore = new SearchStore(webcontainerPromise);

  #editorStore = new EditorStore(this.#filesStore, this);
  #terminalStore = new TerminalStore();

  @hmr()
  #shellActionListeners = new Set<(action: Extract<BoltAction, BaseShellAction>) => void>();

  @hmr()
  #artifactIdList = new Set<string>();

  @hmr()
  #hasUserModifiedFiles = false;

  @hmr()
  rollbackMessageId = atom<string | undefined>();

  @hmr()
  lastRollbackedMessageId = atom<string | undefined>();

  #needsToAddEdgeFunctionDeployAction = false;

  isFixing = computed(this.pendingFixes, (pendingFixes) => Object.keys(pendingFixes).length > 0);

  waitingForImport = computed(this.#filesStore.files, () => !this.#filesStore.fileImportComplete);

  isProcessing = computed(
    [this.isStreaming, this.isFixing, this.isAnalyzing, this.isApplyingAnnotations],
    (isStreaming, isFixing, isAnalyzing, isApplyingAnnotations) => {
      return isStreaming || isFixing || isAnalyzing || isApplyingAnnotations;
    },
  );

  get hasUserModifiedFiles() {
    return this.#hasUserModifiedFiles;
  }

  get artifactList() {
    return [...this.#artifactIdList].map((id) => this.getArtifact(id));
  }

  get runningCommands() {
    const artifacts = Object.values(this.artifacts.get());
    const actions = artifacts.flatMap((artifact) => [...artifact.runner.actions.values()]);

    return actions.filter((action): action is ShellAction => {
      return action.type === 'shell' && action.status.get() === 'running';
    });
  }

  get previews() {
    return this.previewsStore.previews;
  }

  get packageJson() {
    return this.#filesStore.packageJson;
  }

  get allPackageJsonFiles() {
    return this.#filesStore.allPackageJsonFiles;
  }

  get framework() {
    return this.#filesStore.framework;
  }

  get files() {
    return this.#filesStore.files;
  }

  get lockedFiles() {
    return this.#filesStore.lockedFiles;
  }

  get targetedFiles() {
    return this.#filesStore.targetedFiles;
  }

  get migrationFiles() {
    return this.#filesStore.migrationFiles;
  }

  get edgeFunctionsFiles() {
    return this.#filesStore.edgeFunctionsFiles;
  }

  get currentDocument(): ReadableAtom<EditorDocument | undefined> {
    return this.#editorStore.currentDocument;
  }

  get selectedFile(): ReadableAtom<string | undefined> {
    return this.#editorStore.selectedFile;
  }

  get firstArtifact(): ArtifactState | undefined {
    return this.artifactList[0];
  }

  get lastArtifact(): ArtifactState | undefined {
    return this.artifactList.at(-1);
  }

  get filesCount(): number {
    return this.#filesStore.filesCount;
  }

  get showTerminal() {
    return this.#terminalStore.showTerminal;
  }

  get activeTerminal() {
    return this.#terminalStore.activeTerminal;
  }

  get terminals() {
    return this.#terminalStore.terminals;
  }

  get terminalDriver() {
    return this.#terminalStore.terminalDriver;
  }

  get deployDriver() {
    return this.#terminalStore.deployDriver;
  }

  get hasPersistedChanges() {
    return this.#filesStore.binaryUploads.get() === 0 && this.#filesStore.hasPersistedChanges.get();
  }

  get editorHasFocus() {
    return Boolean(this.#editorStore.editorView.get()?.hasFocus);
  }

  readFile(filePath: string) {
    const entry = this.#filesStore.files.get()[addWorkDir(filePath)];

    if (entry?.type !== 'file') {
      throw new Error('EINVAL: Not a file');
    }

    return entry;
  }

  writeFile(filePath: string, file: WorkbenchWritableFile, options?: WriteFileOptions) {
    return this.#filesStore.writeFile(filePath, file, options);
  }

  generateUniqueName(filePath: string, kind: 'file' | 'folder'): string {
    return this.#filesStore.generateUniqueName(filePath, kind);
  }

  setHasUserModifiedFiles(value: boolean) {
    this.#hasUserModifiedFiles = value;
  }

  async copyFile(fromFilePath: string, toFilePath: string) {
    return this.#filesStore.copyFile(fromFilePath, toFilePath);
  }

  removeFile(filePath: string) {
    return this.#filesStore.removeFile(filePath);
  }

  rename(fromPath: string, toPath: string) {
    return this.#filesStore.rename(fromPath, toPath);
  }

  cut(fromFilePath: string, toFilePath: string, kind: 'file' | 'folder') {
    return this.#filesStore.cut(fromFilePath, toFilePath, kind);
  }

  createDirectory(filePath: string, options?: CreateDirectoryOptions) {
    return this.#filesStore.createDirectory(filePath, options);
  }

  async copyDirectory(fromFilePath: string, toFilePath: string) {
    return this.#filesStore.copyDirectory(fromFilePath, toFilePath);
  }

  removeDirectory(filePath: string) {
    return this.#filesStore.removeDirectory(filePath);
  }

  computeContentHash(filePath: string, options?: ContentHashOptions) {
    return this.#filesStore.computeContentHash(filePath, options);
  }

  toggleTerminal(value?: boolean) {
    this.#terminalStore.toggleTerminal(value);
  }

  reloadPreview() {
    this.previewRef?.current?.reload();
  }

  setInspectedElement(element: InspectedElement | null) {
    const previousInspectedElement = this.inspectedElement.get();
    this.inspectedElement.set(element);

    if (!element) {
      webcontainerPromise.then((instance) => {
        instance.internal.previewScript.storage.set({ inspect: false });
      });

      const attachment = chatStore.attachments.get().find(({ file }) => file === previousInspectedElement?.file);

      if (attachment) {
        chatStore.removeAttachment(attachment);
      }

      this.isInspectorOpen.set(false);
    }
  }

  removeFromTargetOrLockedFiles(filePath: string) {
    this.#filesStore.removeFromTargetOrLockedFiles(filePath);
  }

  isAIAllowedToEdit(filePath: string): boolean {
    return this.#filesStore.isAIAllowedToEdit(filePath);
  }

  setFileOrFolderTarget(filePath: string, isTarget: boolean) {
    this.#filesStore.setFileOrFolderTarget(filePath, isTarget);
  }

  setFileOrFolderLocked(filePath: string, isLocked: boolean) {
    this.#filesStore.setFileOrFolderLocked(filePath, isLocked);
  }

  resetAllLockedTargetedFiles() {
    this.#filesStore.resetAllLockedTargetedFiles();
  }

  setFileListenerEnabled(enabled: boolean) {
    this.#filesStore.listenToFilesChanges.set(enabled);
  }

  setDocuments(files: FileMap) {
    this.#editorStore.setDocuments(files);
  }

  setCurrentDocumentContent(newContent: string) {
    const filePath = this.currentDocument.get()?.filePath;

    if (!filePath) {
      return;
    }

    const originalContent = this.#filesStore.getFile(filePath)?.content;
    const unsavedChanges = originalContent !== undefined && originalContent !== newContent;

    this.#editorStore.updateFile(filePath, newContent);

    const currentDocument = this.currentDocument.get();

    if (currentDocument) {
      const previousUnsavedFiles = this.unsavedFiles.get();

      if (
        workbenchStore.isStreaming.get() ||
        workbenchStore.isFixing.get() ||
        (unsavedChanges && previousUnsavedFiles.has(currentDocument.filePath))
      ) {
        return;
      }

      const newUnsavedFiles = new Set(previousUnsavedFiles);

      if (unsavedChanges) {
        newUnsavedFiles.add(currentDocument.filePath);
      } else {
        newUnsavedFiles.delete(currentDocument.filePath);
      }

      this.unsavedFiles.set(newUnsavedFiles);
    }
  }

  setCurrentDocumentScrollPosition(position: ScrollPosition) {
    const editorDocument = this.currentDocument.get();

    if (!editorDocument) {
      return;
    }

    const { filePath } = editorDocument;

    this.#editorStore.updateScrollPosition(filePath, position);
  }

  setSelectedFile(filePath: string | undefined) {
    if (filePath) {
      filePath = addWorkDir(filePath);
    }

    this.#editorStore.setSelectedFile(filePath);
  }

  setSelectedFileProgrammatically(filePath: string | undefined) {
    if (filePath) {
      filePath = addWorkDir(filePath);
    }

    this.#editorStore.setSelectedFileProgrammatically(filePath);
  }

  setStreamingFile(data: PartialFileContent) {
    this.#editorStore.setStreamingFile(data);
  }

  setEditorView(view: EditorView | null) {
    this.#editorStore.editorView.set(view);
  }

  async setActiveLineNumber(lineNumber: number) {
    // wait for the editor view to be initialized
    await this.#editorStore.editorViewReady?.promise;

    const view = this.#editorStore.editorView.get();

    if (!view) {
      return;
    }

    const line = Math.min(lineNumber, view.state.doc.lines);
    const position = view.state.doc.line(line).from;
    const selection = { anchor: position };

    view.dispatch({
      selection,
      scrollIntoView: true,
    });
  }

  setEditorViewReady() {
    this.#editorStore.setEditorViewReady();
  }

  async saveFile(filePath: string) {
    const documents = this.#editorStore.documents.get();
    const document = documents[filePath];

    if (document === undefined) {
      return;
    }

    if (projectStore.shouldFork() && !projectStore.isForking.get()) {
      const slug = projectStore.getSlug();

      invariant(slug);

      forkProject();
    }

    this.#hasUserModifiedFiles = true;

    await this.#filesStore.saveFile(filePath, document.value);

    const newUnsavedFiles = new Set(this.unsavedFiles.get());
    newUnsavedFiles.delete(filePath);

    this.unsavedFiles.set(newUnsavedFiles);
  }

  getStackBlitzResourceConfigStore() {
    return this.#filesStore.stackBlitzResourceConfigFile;
  }

  setCORSProxy(value: boolean) {
    this.#filesStore.assignStackBlitzResourceConfig({
      corsProxy: value,
    });
  }

  async saveCurrentDocument() {
    const currentDocument = this.currentDocument.get();

    if (currentDocument === undefined) {
      return;
    }

    await this.saveFile(currentDocument.filePath);
  }

  resetCurrentDocument() {
    const currentDocument = this.currentDocument.get();

    if (currentDocument === undefined) {
      return;
    }

    const { filePath } = currentDocument;

    const file = this.#filesStore.getFile(filePath);

    if (!file) {
      return;
    }

    this.setCurrentDocumentContent(file.content);
  }

  waitForImport() {
    return this.#filesStore.waitForImport;
  }

  get fileImportComplete() {
    return this.#filesStore.fileImportComplete;
  }

  waitRailsSync() {
    return this.#filesStore.waitRailsSync();
  }

  importProjectFiles(appFiles: AppFiles) {
    this.#filesStore
      .importProjectFiles(appFiles)
      .then(() => {
        const defaultSelectedFile = findDefaultSelectedFile(this.#filesStore.files.get());

        if (defaultSelectedFile) {
          this.#editorStore.selectedFile.set(defaultSelectedFile);
        }
      })
      .catch((error: Error) => {
        toast.error('There was a problem loading files into WebContainer');
        logger.error(error);
      });
  }

  async saveAllFiles() {
    const unsavedFiles = this.unsavedFiles.get();

    for (const filePath of unsavedFiles) {
      await this.saveFile(filePath);
    }

    const hasUserModifiedFiles = this.#hasUserModifiedFiles;

    this.#hasUserModifiedFiles = false;

    return hasUserModifiedFiles;
  }

  abortAllActions() {
    for (const artifact of Object.values(this.artifacts.get())) {
      artifact.runner.abort();
    }
  }

  addArtifact({ title, slug, id, messageId }: ArtifactCallbackData) {
    this.#needsToAddEdgeFunctionDeployAction = false;

    const artifact = this.getArtifact(id);

    if (artifact) {
      return;
    }

    if (!this.hasArtifacts.get()) {
      this.hasArtifacts.set(true);
    }

    this.artifacts.setKey(id, {
      id,
      slug,
      title,
      messageId,
      closed: false,
      runner: new ActionsRunner(),
    });

    this.#artifactIdList.add(id);
  }

  async updateArtifact({ id, messageId }: ArtifactCallbackData, state: Partial<ArtifactUpdateState>, shouldRun = true) {
    if (state.closed && this.#needsToAddEdgeFunctionDeployAction) {
      this.#needsToAddEdgeFunctionDeployAction = false;

      const edgeFunctionDeployAction = createEdgeFunctionDeployAction(id, messageId);

      this.addAction(edgeFunctionDeployAction, shouldRun);
      this.runAction(edgeFunctionDeployAction);

      this.#needsToAddEdgeFunctionDeployAction = false;
    }

    const artifact = this.getArtifact(id);

    if (!artifact) {
      return;
    }

    /**
     * Append the start command to the end of each artifact
     * when closing it.
     */
    if (!artifact.closed && state.closed) {
      await this.#filesStore.waitForImport;

      const framework = this.framework.get();

      if (framework) {
        const alreadyHasStart = [...artifact.runner.actions.values()].some(({ type }) => {
          return type === 'start' || type === 'import';
        });

        if (!alreadyHasStart) {
          const startAction = {
            artifactId: artifact.id,
            messageId,
            action: new StartAction(artifact.id, messageId, `${artifact.id}:restart`, true, framework),
          } satisfies ActionCallbackData;

          this.addAction(startAction, shouldRun);
          this.runAction(startAction);
        }
      }
    }

    this.artifacts.setKey(id, { ...artifact, ...state });
  }

  get actions() {
    const actions: BoltAction[] = [];

    for (const artifact of this.artifactList) {
      for (const action of artifact.runner.actions.values()) {
        actions.push(action);

        if (action instanceof PipeAction) {
          for (const subAction of action.actions.value) {
            actions.push(subAction);
          }
        }
      }
    }

    return actions;
  }

  get shellActions() {
    return this.actions.filter((action) => action instanceof BaseShellAction);
  }

  subscribeToActions(cb: (actions: BoltAction[]) => void) {
    const disposers = new Set<VoidFunction>();

    const dispose = () => {
      disposers.forEach((dispose) => dispose());
      disposers.clear();
    };

    const update = () => {
      dispose();

      disposers.add(workbenchStore.artifacts.listen(update));

      for (const artifact of this.artifactList) {
        disposers.add(artifact.runner.listen(update));
      }

      cb(this.actions);
    };

    update();

    return dispose;
  }

  addAction(data: ActionCallbackData, shouldRun: boolean) {
    const { artifactId } = data;

    if (data.action.type === 'file' && data.action.filePath.startsWith(addWorkDir(SUPABASE_EDGE_FUNCTIONS_FOLDER))) {
      this.#needsToAddEdgeFunctionDeployAction = true;
    }

    if (data.action.type === 'start' && this.#needsToAddEdgeFunctionDeployAction) {
      const edgeFunctionDeployAction = {
        artifactId,
        messageId: data.messageId,
        action: new SupabaseAction(artifactId, data.messageId, `${artifactId}:deploy-edge-function`, 'edge-functions'),
      } satisfies ActionCallbackData;

      this.addAction(edgeFunctionDeployAction, shouldRun);
      this.runAction(edgeFunctionDeployAction);

      this.#needsToAddEdgeFunctionDeployAction = false;
    }

    const artifact = this.getArtifact(artifactId);

    if (artifact.closed) {
      return;
    }

    if (!artifact) {
      unreachable('Artifact not found');
    }

    if (data.action.type === 'shell') {
      const command = data.action.content.value;

      /**
       * If we've already installed, strip the install command from
       * the command since it's not needed. If the resulting command
       * is empty aka it was an install on it's own, skip it entirely.
       */
      if (installs.has(data.artifactId)) {
        const commandWithoutInstall = command.replace(REMOVE_NPM_INSTALL_REGEX, '');

        if (!commandWithoutInstall) {
          return;
        }

        data.action.content.set(commandWithoutInstall);
      }
    }

    artifact.runner.addAction(data, shouldRun);
  }

  async runAction(data: ActionCallbackData) {
    const { artifactId } = data;

    const artifact = this.getArtifact(artifactId);

    if (!artifact) {
      unreachable('Artifact not found');
    }

    await artifact.runner.runAction(data);
  }

  addTerminalOutputListener(fn: (output: string) => void) {
    this.#terminalStore.terminalDriver.addOutputListener(fn);
    this.#terminalStore.deployDriver.addOutputListener(fn);
  }

  addShellActionListener(fn: (action: Extract<BoltAction, BaseShellAction>) => void) {
    this.#shellActionListeners.add(fn);
  }

  runShellAction(shellAction: Extract<BoltAction, BaseShellAction>) {
    const currentTerminal = this.#terminalStore.activeTerminal.get();

    this.#terminalStore.toggleTerminal(true, shellAction.shellId);

    const { promise, resolve, reject } = withResolvers<number>();

    const abortController = new AbortController();

    this.#terminalStore.processCommand({
      shellAction,
      abortController,
      skip: reject,
      onExit: resolve,
    });

    return {
      exit: promise,
      kill: () => {
        abortController.abort();
      },
      done: () => {
        this.#shellActionListeners.forEach((listener) => {
          listener(shellAction);
        });

        this.#terminalStore.activeTerminal.set(currentTerminal);
      },
    };
  }

  getArtifact(id: string) {
    const artifacts = this.artifacts.get();
    return artifacts[id];
  }

  addTerminal() {
    return this.#terminalStore.addTerminal();
  }

  removeTerminal(terminalId: number) {
    this.#terminalStore.remmoveTerminal(terminalId);
  }
}

export const workbenchStore = new WorkbenchStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { workbenchStore });
}

function createEdgeFunctionDeployAction(artifactId: string, messageId: string) {
  return {
    artifactId,
    messageId,
    action: new SupabaseAction(artifactId, messageId, `${artifactId}:deploy-edge-function`, 'edge-functions'),
  } satisfies ActionCallbackData;
}
